import React, { useCallback, useEffect, useState } from 'react';

import { adminAuthHeaders } from './adminCheckoutUtils';

import AdminGiftFinderAnalytics from './AdminGiftFinderAnalytics';
import { AdminPanelHero } from './AdminPanelUi';

import { printAnalyticsReport } from './adminExportUtils';

import {

  downloadAnalyticsCsv,

  formatDelta,

  formatMoney,

  paymentMethodLabel,

} from './analyticsUtils';

import {
  AnalyticsActionBoard,
  AnalyticsAtAGlance,
  AnalyticsCollapsibleSection,
  AnalyticsDailyChart,
  AnalyticsGuide,
  AnalyticsInsightsList,
  AnalyticsJumpNav,
  AnalyticsLoadingState,
  AnalyticsPeriodSummary,
  AnalyticsPipeline,
  AnalyticsQuickPulse,
  AnalyticsRecentOrders,
  AnalyticsSectionTitle,
  AnalyticsShippingCard,
  AnalyticsToolbar,
  AnalyticsViewToggle,
  formatOrderStatusLabel,
  readAnalyticsViewMode,
  writeAnalyticsViewMode,
} from './AdminAnalyticsSections';



function StatCard({ label, value, sub, accent, delta }) {

  const d = delta ? formatDelta(delta) : null;

  return (

    <div className={`admin-analytics-stat${accent ? ` admin-analytics-stat--${accent}` : ''}`}>

      <div className="admin-analytics-stat__label">{label}</div>

      <div className="admin-analytics-stat__value">{value}</div>

      {d && d.tone !== 'muted' ? (

        <div className={`admin-analytics-delta admin-analytics-delta--${d.tone}`} title="Compared with the previous period">

          {d.text} vs last period

        </div>

      ) : null}

      {sub ? <div className="admin-analytics-stat__sub">{sub}</div> : null}

    </div>

  );

}



function isoToday() {

  return new Date().toISOString().slice(0, 10);

}



function daysAgoIso(n) {

  const d = new Date();

  d.setDate(d.getDate() - n);

  return d.toISOString().slice(0, 10);

}



const RANGE_PRESETS = [
  { days: 1, label: 'Today', title: 'Since midnight' },
  { days: 7, label: '7 days', title: 'Last 7 days' },
  { days: 30, label: '30 days', title: 'Last 30 days' },
  { days: 90, label: '90 days', title: 'Last 90 days' },
];



function analyticsQueryToParams(query) {

  const p = new URLSearchParams({ analytics: '1' });

  if (query.mode === 'custom') {

    p.set('from', query.from);

    p.set('to', query.to);

  } else {

    p.set('days', String(query.days));

  }

  return p.toString();

}



function StatusBreakdown({ counts }) {

  const entries = Object.entries(counts || {}).sort((a, b) => b[1] - a[1]);

  const total = entries.reduce((s, [, n]) => s + n, 0) || 1;



  const statusClass = (st) => {

    const s = String(st).toLowerCase();

    if (s === 'paid' || s === 'shipped' || s === 'completed') return 'success';

    if (s === 'processing') return 'primary';

    if (s === 'partially_shipped' || s === 'partially shipped') return 'info';

    if (s === 'pending') return 'warning';

    if (s === 'cancelled') return 'secondary';

    return 'muted';

  };



  if (!entries.length) {

    return <p className="small text-muted mb-0">No orders yet.</p>;

  }



  return (

    <ul className="admin-analytics-status list-unstyled mb-0">

      {entries.map(([status, count]) => (

        <li key={status} className="admin-analytics-status__row">

          <span className={`admin-analytics-status__name text-${statusClass(status)}`}>
            {formatOrderStatusLabel(status)}
          </span>

          <div className="admin-analytics-status__track">

            <div

              className={`admin-analytics-status__fill admin-analytics-status__fill--${statusClass(status)}`}

              style={{ width: `${Math.round((count / total) * 100)}%` }}

            />

          </div>

          <span className="admin-analytics-status__count">{count}</span>

        </li>

      ))}

    </ul>

  );

}



function OnlinePaymentsSummary({ online, rangeLabel }) {
  if (!online) return null;
  const pending = online.pending_count ?? 0;
  const paidRz = online.period_paid_razorpay_orders ?? 0;
  const paidRzRev = online.period_paid_razorpay_revenue ?? 0;
  const paidOnline = online.period_paid_online_orders ?? 0;
  if (pending === 0 && paidRz === 0 && paidOnline === 0) {
    return (
      <p className="small text-muted mb-0">
        No Razorpay checkout activity in {rangeLabel}. Configure keys under Site → Checkout.
      </p>
    );
  }
  return (
    <ul className="list-unstyled small mb-0 admin-analytics-online-pay">
      {pending > 0 ? (
        <li className="mb-2">
          <strong className="text-warning">{pending}</strong> checkout{pending === 1 ? '' : 's'} started but not paid yet
          {online.pending_value > 0 ? ` (${formatMoney(online.pending_value)})` : ''}. Check Orders → filter Pending.
        </li>
      ) : null}
      {paidRz > 0 ? (
        <li className="mb-2">
          <strong>{paidRz}</strong> paid via Razorpay in {rangeLabel} ({formatMoney(paidRzRev)}). Payment IDs are on
          each order in Orders.
        </li>
      ) : null}
      {paidOnline > paidRz ? (
        <li className="mb-0 text-muted">
          {paidOnline - paidRz} paid order{paidOnline - paidRz === 1 ? '' : 's'} marked online without a stored
          Razorpay payment id (manual mark paid or older data).
        </li>
      ) : null}
    </ul>
  );
}

function PaymentMix({ methods, totalRevenue }) {

  if (!methods?.length) {

    return <p className="small text-muted mb-0">No paid orders in this period.</p>;

  }

  const total = totalRevenue > 0 ? totalRevenue : methods.reduce((s, m) => s + m.revenue, 0) || 1;



  return (

    <ul className="admin-analytics-mix list-unstyled mb-0">

      {methods.map((m) => {

        const pct = Math.round((m.revenue / total) * 100);

        return (

          <li key={m.method} className="admin-analytics-mix__row">

            <div className="d-flex justify-content-between small mb-1">

              <span>{paymentMethodLabel(m.method)}</span>

              <span>

                {formatMoney(m.revenue)} · {m.orders} order{m.orders === 1 ? '' : 's'}

              </span>

            </div>

            <div className="admin-analytics-mix__track">

              <div className="admin-analytics-mix__fill" style={{ width: `${Math.max(pct, 4)}%` }} />

            </div>

          </li>

        );

      })}

    </ul>

  );

}



function RankedTable({ columns, rows, emptyMessage }) {

  if (!rows?.length) {

    return <p className="small text-muted mb-0">{emptyMessage}</p>;

  }

  return (

    <div className="table-responsive">

      <table className="table table-sm align-middle mb-0 admin-analytics-table">

        <thead>

          <tr>

            {columns.map((c) => (

              <th key={c.key} className={c.align === 'end' ? 'text-end' : ''}>

                {c.label}

              </th>

            ))}

          </tr>

        </thead>

        <tbody>

          {rows.map((row, i) => (

            <tr key={row.key ?? i}>

              {columns.map((c) => (

                <td key={c.key} className={c.align === 'end' ? 'text-end' : ''}>

                  {c.render(row)}

                </td>

              ))}

            </tr>

          ))}

        </tbody>

      </table>

    </div>

  );

}



export default function AdminAnalyticsPanel({ sessionToken, onGoOrders, onGoReviews }) {

  const [rangeQuery, setRangeQuery] = useState({ mode: 'preset', days: 30 });

  const [customFrom, setCustomFrom] = useState(() => daysAgoIso(6));

  const [customTo, setCustomTo] = useState(() => isoToday());

  const [chartMetric, setChartMetric] = useState('revenue');

  const [viewMode, setViewMode] = useState(readAnalyticsViewMode);

  const [data, setData] = useState(null);

  const [error, setError] = useState('');

  const [loading, setLoading] = useState(true);



  const load = useCallback(async () => {

    setLoading(true);

    setError('');

    try {

      const qs = analyticsQueryToParams(rangeQuery);

      const r = await fetch(`/api/admin_orders.php?${qs}`, {

        headers: adminAuthHeaders(sessionToken),

      });

      const body = await r.json().catch(() => ({}));

      if (!r.ok || !body.success) {

        setError(body.error || 'Could not load analytics.');

        setData(null);

        return;

      }

      setData(body.analytics || null);

    } catch {

      setError('Could not load analytics.');

      setData(null);

    } finally {

      setLoading(false);

    }

  }, [sessionToken, rangeQuery]);



  useEffect(() => {

    if (sessionToken) load();

  }, [sessionToken, load]);



  const applyCustomRange = (e) => {

    e?.preventDefault?.();

    const from = String(customFrom || '').trim();

    const to = String(customTo || '').trim();

    if (!from || !to) {

      setError('Choose both start and end dates.');

      return;

    }

    if (from > to) {

      setError('Start date must be on or before end date.');

      return;

    }

    setError('');

    setRangeQuery({ mode: 'custom', from, to });

  };



  const presetActive = (days) => rangeQuery.mode === 'preset' && rangeQuery.days === days;

  const customActive = rangeQuery.mode === 'custom';

  const rangeLabel = data?.range?.label || (rangeQuery.mode === 'preset' ? `Last ${rangeQuery.days} days` : 'Custom range');

  const period = data?.period || {};

  const cmp = data?.comparison || {};

  const prevLabel = cmp.previous_range?.label || 'prior period';

  const chartFrom = data?.range?.from;

  const chartTo = data?.range?.to;



  const rev = data?.revenue || {};

  const ord = data?.orders_paid || {};

  const cust = data?.customers || {};

  const custPeriod = data?.customers_period || {};

  const health = data?.health || {};

  const discounts = data?.discounts || {};

  const recent = Array.isArray(data?.recent_orders) ? data.recent_orders : [];

  const fulfillment = data?.fulfillment || {};

  const shipmentStats = data?.shipments || {};

  const shippingInsights = data?.shipping_insights || {};

  const pipeline = data?.pipeline || [];

  const actionItems = data?.actions || [];

  const periodActivity = data?.period_activity || {};



  return (

    <div className="admin-analytics admin-panel-workspace-root">

      <AdminPanelHero
        title="How's the shop doing?"
        lead="A simple snapshot of sales, shipping, and what deserves your attention — pick a date range to explore."
        actions={
          <AnalyticsToolbar
            rangePresets={RANGE_PRESETS}
            presetActive={presetActive}
            onPreset={(days) => setRangeQuery({ mode: 'preset', days })}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFrom={setCustomFrom}
            onCustomTo={setCustomTo}
            customActive={customActive}
            onApplyCustom={applyCustomRange}
            loading={loading}
            hasData={Boolean(data)}
            onRefresh={load}
            onExportCsv={() => downloadAnalyticsCsv(data, rangeLabel)}
            onPrint={() => printAnalyticsReport(data, rangeLabel)}
          />
        }
      />

      {error ? <div className="alert alert-danger py-2 small">{error}</div> : null}

      {loading && !data ? <AnalyticsLoadingState /> : null}

      {!data && !loading && !error ? (
        <div className="card border-0 shadow-sm mb-3 admin-analytics-empty">
          <div className="card-body py-4 text-center">
            <p className="h6 mb-2">No shop data yet</p>
            <p className="small text-muted mb-3">
              When customers place orders, you&apos;ll see revenue, trends, and action items here.
            </p>
            <button type="button" className="btn btn-primary btn-sm rounded-pill" onClick={onGoOrders}>
              Go to orders
            </button>
          </div>
        </div>
      ) : null}

      {data ? (
        <>
          <AnalyticsAtAGlance
            data={data}
            rangeLabel={rangeLabel}
            prevLabel={prevLabel}
            onGoOrders={onGoOrders}
          />

          <AnalyticsActionBoard actions={actionItems} onGoOrders={onGoOrders} onGoReviews={onGoReviews} />

          <AnalyticsInsightsList data={data} />

          <AnalyticsViewToggle
            viewMode={viewMode}
            onChange={(mode) => {
              setViewMode(mode);
              writeAnalyticsViewMode(mode);
            }}
          />

          <AnalyticsJumpNav viewMode={viewMode} />

          <AnalyticsQuickPulse revenue={rev} orders={ord} avgOrderValue={data?.avg_order_value} />

      {viewMode === 'detailed' ? <AnalyticsGuide /> : null}

      {viewMode === 'detailed' ? (
        <>
      <AnalyticsSectionTitle hint={`Change % compares with the same-length period just before ${rangeLabel}.`}>
        Deeper dive · {rangeLabel}
      </AnalyticsSectionTitle>

      <div id="analytics-period" className="card border-0 shadow-sm mb-3">
        <div className="card-body py-2">
          <AnalyticsPeriodSummary periodActivity={periodActivity} health={health} />
        </div>
      </div>

      <div className="row g-3 mb-3">

        <div className="col-sm-6 col-md-4 col-xl-2">

          <StatCard

            label={`Revenue · ${rangeLabel}`}

            value={formatMoney(period.revenue)}

            delta={cmp.revenue_pct}

            sub={`Prior: ${formatMoney(cmp.previous?.revenue)}`}

          />

        </div>

        <div className="col-sm-6 col-md-4 col-xl-2">

          <StatCard

            label="Paid orders"

            value={period.orders ?? 0}

            delta={cmp.orders_pct}

            sub={`Prior: ${cmp.previous?.orders ?? 0}`}

          />

        </div>

        <div className="col-sm-6 col-md-4 col-xl-2">

          <StatCard

            label="Avg order value"

            value={formatMoney(period.aov)}

            delta={cmp.aov_pct}

            sub={`All-time avg ${formatMoney(data?.avg_order_value)}`}

          />

        </div>

        <div className="col-sm-6 col-md-4 col-xl-2">

          <StatCard

            label="Units sold"

            value={period.units_sold ?? 0}

            sub={`${period.items_per_order ?? 0} items / order`}

          />

        </div>

        <div className="col-sm-6 col-md-4 col-xl-2">

          <StatCard

            label="Repeat orders"

            value={`${custPeriod.repeat_rate_pct ?? 0}%`}

            sub={`${custPeriod.returning_orders ?? 0} from returning · ${custPeriod.new ?? 0} new customers`}

          />

        </div>

        <div className="col-sm-6 col-md-4 col-xl-2">

          <StatCard

            label="Cancellation rate"

            value={`${health.cancellation_rate_pct ?? 0}%`}

            sub={`${health.cancelled ?? 0} cancelled · ${health.placed ?? 0} placed`}

            accent={health.cancellation_rate_pct > 10 ? 'warn' : undefined}

          />

        </div>

      </div>
        </>
      ) : null}

      <div id="analytics-trend" className="row g-3 mb-3">

        <div className="col-lg-8">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">

                <div>
                  <h3 className="h6 mb-0">Daily trend · {rangeLabel}</h3>
                  <p className="small text-muted mb-0">Tallest bar is your best day in this range</p>
                </div>

                <div className="btn-group btn-group-sm" role="group" aria-label="Chart metric">

                  <button

                    type="button"

                    className={`btn btn-outline-secondary${chartMetric === 'revenue' ? ' active' : ''}`}

                    onClick={() => setChartMetric('revenue')}

                  >

                    Revenue

                  </button>

                  <button

                    type="button"

                    className={`btn btn-outline-secondary${chartMetric === 'orders' ? ' active' : ''}`}

                    onClick={() => setChartMetric('orders')}

                  >

                    Orders

                  </button>

                </div>

              </div>

              {loading && !data?.daily ? (
                <p className="small text-muted mb-0">Loading chart…</p>
              ) : (
                <AnalyticsDailyChart
                  daily={data?.daily}
                  from={chartFrom}
                  to={chartTo}
                  metric={chartMetric}
                />
              )}

            </div>

          </div>

        </div>

        <div className="col-lg-4">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <AnalyticsSectionTitle hint="Live snapshot of all open orders — not filtered by period.">
                Order pipeline
              </AnalyticsSectionTitle>

              <AnalyticsPipeline pipeline={pipeline} />

            </div>

          </div>

        </div>

      </div>

      {viewMode === 'detailed' ? (
        <>
      <div id="analytics-operations" className="row g-3 mb-3">

        <div className="col-lg-7">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <AnalyticsSectionTitle hint="Delivery income and speed for paid orders in this period.">
                Shipping &amp; delivery · {rangeLabel}
              </AnalyticsSectionTitle>

              <AnalyticsShippingCard
                shipping={shippingInsights}
                shipments={shipmentStats}
                rangeLabel={rangeLabel}
              />

            </div>

          </div>

        </div>

        <div className="col-lg-5">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <h3 className="h6 mb-3">All orders by status</h3>

              <StatusBreakdown counts={data?.counts_by_status} />

              <hr className="my-3" />

              <p className="small fw-semibold text-muted mb-2">Fulfillment snapshot (now)</p>

              <div className="row g-2 small">

                <div className="col-6">

                  <span className="text-muted d-block">Paid (not shipped)</span>

                  <strong>{fulfillment.paid ?? 0}</strong>

                </div>

                <div className="col-6">

                  <span className="text-muted d-block">Processing</span>

                  <strong>{fulfillment.processing ?? 0}</strong>

                </div>

                <div className="col-6">

                  <span className="text-muted d-block">Partially shipped</span>

                  <strong className="text-info">{fulfillment.partially_shipped ?? 0}</strong>

                </div>

                <div className="col-6">

                  <span className="text-muted d-block">Fully shipped</span>

                  <strong>{fulfillment.shipped ?? 0}</strong>

                </div>

                <div className="col-6">

                  <span className="text-muted d-block">Completed</span>

                  <strong>{fulfillment.completed ?? 0}</strong>

                </div>

                {shipmentStats.enabled ? (
                  <>
                    <div className="col-12">
                      <hr className="my-1 opacity-25" />
                      <span className="text-muted d-block">Parcels dispatched (this period)</span>
                      <strong>{shipmentStats.parcels_in_period ?? 0}</strong>
                      <span className="text-muted">
                        {' '}
                        · {shipmentStats.units_shipped_in_period ?? 0} unit
                        {(shipmentStats.units_shipped_in_period ?? 0) === 1 ? '' : 's'} shipped
                      </span>
                    </div>
                  </>
                ) : null}

                <div className="col-6">

                  <span className="text-muted d-block">Customers</span>

                  <strong>{cust.total ?? 0}</strong>

                  <span className="text-muted"> (+{cust.new_week ?? 0} this week)</span>

                </div>

                <div className="col-6">

                  <span className="text-muted d-block">With orders</span>

                  <strong>{cust.with_orders ?? 0}</strong>

                </div>

                {(data?.coupons_used_month ?? 0) > 0 ? (

                  <div className="col-12">

                    <span className="text-muted d-block">Coupons used (30d)</span>

                    <strong>{data.coupons_used_month}</strong>

                  </div>

                ) : null}

                {(discounts.orders ?? 0) > 0 ? (

                  <div className="col-12">

                    <span className="text-muted d-block">Discounts in period</span>

                    <strong>

                      {formatMoney(discounts.total_amount)} ({discounts.orders} orders, {discounts.pct_of_revenue}% of

                      revenue)

                    </strong>

                  </div>

                ) : null}

              </div>

            </div>

          </div>

        </div>

      </div>



      <AnalyticsSectionTitle hint="Where customers pay and ship to in the selected period.">
        Payments &amp; geography
      </AnalyticsSectionTitle>

      <div id="analytics-payments" className="row g-3 mb-3">

        <div className="col-md-6">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <h3 className="h6 mb-3">Payment mix · {rangeLabel}</h3>

              <PaymentMix methods={data?.payment_methods} totalRevenue={period.revenue} />

            </div>

          </div>

        </div>

        <div className="col-md-6">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <h3 className="h6 mb-3">Razorpay · {rangeLabel}</h3>

              <OnlinePaymentsSummary online={data?.online_payments} rangeLabel={rangeLabel} />

            </div>

          </div>

        </div>

        <div className="col-md-6">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <h3 className="h6 mb-3">Geography · {rangeLabel}</h3>

              <p className="small fw-semibold text-muted mb-2">Top cities</p>

              <RankedTable

                emptyMessage="No shipping cities in this period."

                columns={[

                  { key: 'city', label: 'City', render: (c) => c.city },

                  { key: 'orders', label: 'Orders', align: 'end', render: (c) => c.orders },

                  {

                    key: 'revenue',

                    label: 'Revenue',

                    align: 'end',

                    render: (c) => <span className="fw-semibold">{formatMoney(c.revenue)}</span>,

                  },

                ]}

                rows={(data?.top_cities || []).map((c) => ({ ...c, key: c.city }))}

              />

              <p className="small fw-semibold text-muted mb-2 mt-3">Top states</p>

              <RankedTable

                emptyMessage="No state data in this period."

                columns={[

                  { key: 'state', label: 'State', render: (c) => c.state },

                  { key: 'orders', label: 'Orders', align: 'end', render: (c) => c.orders },

                  {

                    key: 'revenue',

                    label: 'Revenue',

                    align: 'end',

                    render: (c) => <span className="fw-semibold">{formatMoney(c.revenue)}</span>,

                  },

                ]}

                rows={(data?.top_states || []).map((c) => ({ ...c, key: c.state }))}

              />

            </div>

          </div>

        </div>

      </div>

      <AnalyticsSectionTitle hint="Best sellers and latest activity.">
        Products &amp; recent orders
      </AnalyticsSectionTitle>
        </>
      ) : null}

      <div className="row g-3 mb-3" id="analytics-products">

        {viewMode === 'detailed' ? (
        <div className="col-lg-4">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-header bg-transparent border-0 pb-0">

              <h3 className="h6 mb-0">Top products · {rangeLabel}</h3>

            </div>

            <div className="card-body pt-2">

              <RankedTable

                emptyMessage="No paid orders in this period."

                columns={[

                  {

                    key: 'name',

                    label: 'Product',

                    render: (p) => (

                      <>

                        <span className="d-block text-truncate" style={{ maxWidth: '10rem' }} title={p.product_name}>

                          {p.product_name}

                        </span>

                        <span className="small text-muted">{p.category_slug}</span>

                      </>

                    ),

                  },

                  { key: 'qty', label: 'Qty', align: 'end', render: (p) => p.quantity },

                  {

                    key: 'rev',

                    label: 'Revenue',

                    align: 'end',

                    render: (p) => <span className="fw-semibold">{formatMoney(p.revenue)}</span>,

                  },

                ]}

                rows={(data?.top_products || []).map((p, i) => ({

                  ...p,

                  key: `${p.category_slug}-${p.product_name}-${i}`,

                }))}

              />

            </div>

          </div>

        </div>
        ) : null}



        {viewMode === 'detailed' ? (
        <div className="col-lg-4">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-header bg-transparent border-0 pb-0">

              <h3 className="h6 mb-0">Top categories · {rangeLabel}</h3>

            </div>

            <div className="card-body pt-2">

              <RankedTable

                emptyMessage="No category sales in this period."

                columns={[

                  {

                    key: 'cat',

                    label: 'Category',

                    render: (c) => <span className="text-capitalize">{c.category_slug.replace(/-/g, ' ')}</span>,

                  },

                  { key: 'qty', label: 'Qty', align: 'end', render: (c) => c.quantity },

                  {

                    key: 'rev',

                    label: 'Revenue',

                    align: 'end',

                    render: (c) => <span className="fw-semibold">{formatMoney(c.revenue)}</span>,

                  },

                ]}

                rows={(data?.top_categories || []).map((c) => ({ ...c, key: c.category_slug }))}

              />

            </div>

          </div>

        </div>
        ) : null}

        <div className={viewMode === 'detailed' ? 'col-lg-4' : 'col-12'}>
          <AnalyticsRecentOrders orders={recent} onGoOrders={onGoOrders} />
        </div>
      </div>

      <AnalyticsCollapsibleSection
        id="analytics-gift"
        title="Gift finder wizard"
        hint="How customers use /gift-finder — same date range as above"
        defaultOpen={viewMode === 'detailed'}
      >
        <AdminGiftFinderAnalytics sessionToken={sessionToken} rangeQuery={rangeQuery} />
      </AnalyticsCollapsibleSection>
        </>
      ) : null}

    </div>

  );

}


